
import { Form, Json, Lead, FormField, FormStyling, WelcomeSettings, OfferingSettings } from "@/types";
import { getDefaultFormTemplate } from "./form-utils";

// Parse form data from Supabase to our Form model
export function parseFormData(data: any, defaultTemplate: Form): Form {
  if (!data) return defaultTemplate;
  
  return {
    id: data.id || defaultTemplate.id,
    name: data.name || defaultTemplate.name,
    description: data.description || defaultTemplate.description,
    fields: data.fields || defaultTemplate.fields,
    styling: data.styling || defaultTemplate.styling,
    createdAt: data.created_at ? new Date(data.created_at) : new Date(),
    updatedAt: data.updated_at ? new Date(data.updated_at) : new Date(),
    userId: data.user_id || defaultTemplate.userId,
    isPublished: data.is_published || false,
    isArchived: data.is_archived || false,
    views: data.views || 0,
    showBranding: data.show_branding || defaultTemplate.showBranding,
    embedCode: data.embed_code || defaultTemplate.embedCode,
    content_settings: data.content_settings || defaultTemplate.content_settings,
    welcome_settings: data.welcome_settings || defaultTemplate.welcome_settings,
    offering_settings: data.offering_settings || defaultTemplate.offering_settings
  };
}

// Parse lead data from JSON to a structured format
export function parseLeadData(data: Json): Record<string, string> {
  if (!data) return {};
  
  // If data is already an object, return it as is
  if (typeof data === 'object' && data !== null) {
    const result: Record<string, string> = {};
    
    // Convert all values to strings for consistency
    Object.entries(data as Record<string, any>).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === 'object') {
          result[key] = JSON.stringify(value);
        } else {
          result[key] = String(value);
        }
      }
    });
    
    return result;
  }
  
  // If data is a string, try to parse it as JSON
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parseLeadData(parsed); // Recursively parse the parsed data
    } catch (e) {
      console.error("Error parsing lead data:", e);
      return { data: data as string };
    }
  }
  
  return {};
}

// Convert our Form model to a format that Supabase expects
export function convertFormToSupabaseFormat(form: Partial<Form>): any {
  // Handle partial form objects with only needed fields
  const formData: any = { 
    // Only include properties that exist in the form object
  };
  
  // Map properties with different names in the database
  if (form.id !== undefined) formData.id = form.id;
  if (form.name !== undefined) formData.name = form.name;
  if (form.description !== undefined) formData.description = form.description;
  if (form.userId !== undefined) formData.user_id = form.userId;
  if (form.fields !== undefined) formData.fields = convertToJson(form.fields);
  if (form.styling !== undefined) formData.styling = convertToJson(form.styling);
  if (form.isPublished !== undefined) formData.is_published = form.isPublished;
  if (form.isArchived !== undefined) formData.is_archived = form.isArchived;
  if (form.views !== undefined) formData.views = form.views;
  if (form.showBranding !== undefined) formData.show_branding = form.showBranding;
  if (form.embedCode !== undefined) formData.embed_code = form.embedCode;
  if (form.content_settings !== undefined) formData.content_settings = convertToJson(form.content_settings);
  if (form.welcome_settings !== undefined) formData.welcome_settings = convertToJson(form.welcome_settings);
  if (form.offering_settings !== undefined) formData.offering_settings = convertToJson(form.offering_settings);
  
  // Always update the timestamp when modifying a form
  formData.updated_at = new Date().toISOString();
  
  return formData;
}

// Helper function to properly type the form fields for Supabase
export function convertToJson<T>(data: T): Json {
  // Use a two-step process to safely convert to Json type:
  // First cast to unknown, then to Json
  return data as unknown as Json;
}
